import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { X, CookieIcon, Settings } from "lucide-react";
import { Link } from 'react-router-dom';

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

export const CookieConsent = () => {
  console.log("CookieConsent component rendering");
  
  const { language } = useLanguage();
  const [showBanner, setShowBanner] = useState(true); // Always start with visible banner
  const [showSettings, setShowSettings] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [cookieSettings, setCookieSettings] = useState<CookieSettings>({
    necessary: true, // Always true
    analytics: false,
    marketing: false,
    preferences: false,
  });

  // Check if consent has been given on component mount
  useEffect(() => {
    console.log("CookieConsent mounted, checking for existing consent");
    // Check for existing consent in localStorage
    const hasConsentStored = localStorage.getItem('cookie-consent');
    console.log("Stored consent:", hasConsentStored);
    
    // Only hide the banner if user has given consent before
    if (hasConsentStored === 'true') {
      console.log("Consent found, hiding banner");
      setShowBanner(false);
      setHasConsent(true);
      
      // Apply saved settings if they exist
      try {
        const savedSettings = JSON.parse(localStorage.getItem('cookie-settings') || '{}');
        if (Object.keys(savedSettings).length > 0) {
          console.log("Applying saved cookie settings:", savedSettings);
          setCookieSettings(prevSettings => ({
            ...prevSettings,
            ...savedSettings
          }));
          
          // GTM is already loaded in index.html, we just need to set the consent state
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'cookie_consent_update',
              cookie_consent: {
                analytics: savedSettings.analytics || false,
                marketing: savedSettings.marketing || false,
                preferences: savedSettings.preferences || false
              }
            });
            console.log('Updated GTM consent state');
          }
        }
      } catch (e) {
        console.error('Error parsing cookie settings:', e);
      }
    } else {
      console.log("No consent found, showing banner");
    }
  }, []);

  const handleAcceptAll = () => {
    const newSettings = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    
    setCookieSettings(newSettings);
    saveSettings(newSettings);
    setShowBanner(false);
    setHasConsent(true);
    
    // Send updated consent to GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'cookie_consent_update',
        cookie_consent: {
          analytics: true,
          marketing: true,
          preferences: true
        }
      });
      console.log('Updated GTM consent state - all accepted');
    }
  };

  const handleAcceptNecessary = () => {
    const newSettings = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    
    setCookieSettings(newSettings);
    saveSettings(newSettings);
    setShowBanner(false);
    setHasConsent(true);
    
    // Send updated consent to GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'cookie_consent_update',
        cookie_consent: {
          analytics: false,
          marketing: false,
          preferences: false
        }
      });
      console.log('Updated GTM consent state - only necessary');
    }
  };

  const handleSaveSettings = () => {
    saveSettings(cookieSettings);
    setShowBanner(false);
    setShowSettings(false);
    setHasConsent(true);
    
    // Send updated consent to GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'cookie_consent_update',
        cookie_consent: {
          analytics: cookieSettings.analytics,
          marketing: cookieSettings.marketing,
          preferences: cookieSettings.preferences
        }
      });
      console.log('Updated GTM consent state - custom settings');
    }
  };

  const saveSettings = (settings: CookieSettings) => {
    console.log("Saving cookie settings:", settings);
    localStorage.setItem('cookie-consent', 'true');
    localStorage.setItem('cookie-settings', JSON.stringify(settings));
    document.dispatchEvent(new Event('cookieConsentUpdated'));
  };

  const toggleSetting = (key: keyof CookieSettings) => {
    if (key === 'necessary') return; // Necessary cookies can't be toggled
    
    setCookieSettings({
      ...cookieSettings,
      [key]: !cookieSettings[key],
    });
  };

  return (
    <>
      {/* Fixed cookie preferences button - ALWAYS VISIBLE */}
      <div 
        onClick={() => setShowSettings(true)}
        className="fixed bottom-4 left-4 z-[9999] bg-white shadow-md hover:shadow-lg rounded-full p-3 transition-all duration-200 border border-blue-200 flex items-center gap-2 cursor-pointer"
        style={{ 
          opacity: showBanner ? 0 : 1, 
          visibility: showBanner ? 'hidden' : 'visible',
          transition: 'opacity 0.3s ease, visibility 0.3s ease' 
        }}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          className="text-blue-600"
        >
          <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"></path>
          <circle cx="7" cy="7" r="1"></circle>
          <circle cx="12" cy="12" r="1"></circle>
          <circle cx="17" cy="17" r="1"></circle>
        </svg>
        <span className="text-sm font-medium text-blue-600 pr-1">
          {language === 'en' ? 'Cookies' : 'Cookies'}
        </span>
      </div>

      {/* Main cookie banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white shadow-lg border-t border-gray-200 p-4">
          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-bold text-lg text-gray-900">
                  {language === 'en' ? 'Cookie Consent' : 'Cookie Toestemming'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {language === 'en' 
                    ? 'We use cookies to improve your experience. You can choose which cookies you want to allow.' 
                    : 'We gebruiken cookies om uw ervaring te verbeteren. U kunt kiezen welke cookies u wilt toestaan.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 mt-2 md:mt-0">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSettings(true)}
                  className="border-gray-300 hover:bg-gray-50"
                >
                  {language === 'en' ? 'Preferences' : 'Voorkeuren'}
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleAcceptNecessary}
                  className="border-gray-300 hover:bg-gray-50"
                >
                  {language === 'en' ? 'Necessary Only' : 'Alleen Noodzakelijk'}
                </Button>
                <Button 
                  variant="default"
                  size="sm"
                  onClick={handleAcceptAll}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {language === 'en' ? 'Accept All' : 'Accepteer Alle'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie settings modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom-10 duration-300">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-bold text-xl">
                  {language === 'en' ? 'Cookie Settings' : 'Cookie Instellingen'}
                </h3>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setShowSettings(false)}
                  className="h-8 w-8 rounded-full"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <p className="text-gray-600 mb-6">
                {language === 'en' 
                  ? 'Manage your cookie preferences. Necessary cookies are always enabled as they are essential for the website to function properly.' 
                  : 'Beheer uw cookie-voorkeuren. Noodzakelijke cookies zijn altijd ingeschakeld omdat ze essentieel zijn voor het goed functioneren van de website.'}
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-3">
                  <div>
                    <h4 className="font-medium">
                      {language === 'en' ? 'Necessary' : 'Noodzakelijk'}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {language === 'en' 
                        ? 'Required for the website to function properly.' 
                        : 'Vereist voor het goed functioneren van de website.'}
                    </p>
                  </div>
                  <div className="h-5 w-10 bg-blue-500 rounded-full relative">
                    <div className="absolute right-1 top-1 bg-white h-3 w-3 rounded-full"></div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between border-b pb-3">
                  <div>
                    <h4 className="font-medium">
                      {language === 'en' ? 'Analytics' : 'Analytisch'}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {language === 'en' 
                        ? 'Help us improve by collecting anonymous usage data.' 
                        : 'Help ons verbeteren door anonieme gebruiksgegevens te verzamelen.'}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleSetting('analytics')}
                    className={`h-5 w-10 rounded-full relative transition-colors ${
                      cookieSettings.analytics ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    aria-checked={cookieSettings.analytics}
                    role="switch"
                    aria-label={language === 'en' ? 'Toggle analytics cookies' : 'Schakel analytische cookies in/uit'}
                  >
                    <div 
                      className={`absolute top-1 h-3 w-3 bg-white rounded-full transition-all ${
                        cookieSettings.analytics ? 'right-1' : 'left-1'
                      }`}
                    ></div>
                  </button>
                </div>
                
                <div className="flex items-center justify-between border-b pb-3">
                  <div>
                    <h4 className="font-medium">
                      {language === 'en' ? 'Marketing' : 'Marketing'}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {language === 'en' 
                        ? 'Allow us to personalize the content and ads you see.' 
                        : 'Sta ons toe om de inhoud en advertenties die u ziet te personaliseren.'}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleSetting('marketing')}
                    className={`h-5 w-10 rounded-full relative transition-colors ${
                      cookieSettings.marketing ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    aria-checked={cookieSettings.marketing}
                    role="switch"
                    aria-label={language === 'en' ? 'Toggle marketing cookies' : 'Schakel marketing cookies in/uit'}
                  >
                    <div 
                      className={`absolute top-1 h-3 w-3 bg-white rounded-full transition-all ${
                        cookieSettings.marketing ? 'right-1' : 'left-1'
                      }`}
                    ></div>
                  </button>
                </div>
                
                <div className="flex items-center justify-between pb-3">
                  <div>
                    <h4 className="font-medium">
                      {language === 'en' ? 'Preferences' : 'Voorkeuren'}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {language === 'en' 
                        ? 'Allow the website to remember your preferences and settings.' 
                        : 'Laat de website uw voorkeuren en instellingen onthouden.'}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleSetting('preferences')}
                    className={`h-5 w-10 rounded-full relative transition-colors ${
                      cookieSettings.preferences ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    aria-checked={cookieSettings.preferences}
                    role="switch"
                    aria-label={language === 'en' ? 'Toggle preference cookies' : 'Schakel voorkeur cookies in/uit'}
                  >
                    <div 
                      className={`absolute top-1 h-3 w-3 bg-white rounded-full transition-all ${
                        cookieSettings.preferences ? 'right-1' : 'left-1'
                      }`}
                    ></div>
                  </button>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <Button 
                  variant="outline"
                  onClick={() => setShowSettings(false)}
                >
                  {language === 'en' ? 'Cancel' : 'Annuleren'}
                </Button>
                <Button 
                  variant="default"
                  onClick={handleSaveSettings}
                >
                  {language === 'en' ? 'Save Settings' : 'Instellingen Opslaan'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent; 